import {
  CDN_BUILD_URL_ENV_VAR,
  CDN_BUILD_CONTAINER_NAME_ENV_VAR,
  CDN_MEDIA_URL_ENV_VAR,
} from "./cdn.constants";

/**
 * Returns the configured build CDN base URL with the container name appended,
 * ensuring no duplicate container path segments and properly trimmed slashes.
 */
export const getCdnBuildBaseUrl = (
  baseUrl = process.env[CDN_BUILD_URL_ENV_VAR],
  container = process.env[CDN_BUILD_CONTAINER_NAME_ENV_VAR],
): string => {
  const rawUrl = (baseUrl || "").trim();

  if (!rawUrl) return "";

  const cleanBase = rawUrl.replace(/\/+$/, "");

  const containerName = (container || "")
    .trim()
    .replace(/^\/+|\/+$/g, "");

  if (containerName && !cleanBase.endsWith(`/${containerName}`)) {
    return `${cleanBase}/${containerName}`;
  }

  return cleanBase;
};

/**
 * Prepend the Build CDN base URL and container path to public asset paths.
 * Returns the absolute CDN URL when configured, or falls back to the root-relative path if not set.
 */
export const getCdnAssetUrl = (path?: string | null, baseUrl?: string): string => {
  if (!path) return "";
  const trimmed = path.trim();
  if (!trimmed) return "";

  // If already an absolute URL or data URL, leave unchanged
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:")
  ) {
    return trimmed;
  }

  const cdnBase = baseUrl ?? getCdnBuildBaseUrl();
  const cleanPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

  return cdnBase ? `${cdnBase}${cleanPath}` : cleanPath;
};

/**
 * Prepend the Media CDN base URL to dynamic media paths (creators, company logos).
 * If already an absolute URL or data URL, leaves unchanged.
 */
export const getMediaAssetUrl = (path?: string | null): string => {
  if (!path) return "";
  const trimmed = path.trim();
  if (!trimmed) return "";

  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:")
  ) {
    return trimmed;
  }

  const mediaCdn = process.env[CDN_MEDIA_URL_ENV_VAR]?.trim().replace(/\/+$/, "");
  const cleanPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

  return mediaCdn ? `${mediaCdn}${cleanPath}` : cleanPath;
};
